// InvoiceDetails.js - Complete Code with Email Functionality
import { FaDownload, FaChevronLeft, FaPrint, FaShareAlt, FaEnvelope } from "react-icons/fa";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Stack } from "@mui/material";
import { useCurrency } from '../currency/currencyContext';
// import ShareModal from './ShareModal';

const InvoiceDetails = ({ orders }) => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { formatPrice, getCurrencySymbol, convertPrice, getCurrentCurrency } = useCurrency();
  const [company, setCompany] = useState(null);
  // const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  
  // Email states
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [emailNote, setEmailNote] = useState(''); // NEW: optional note for the email

  useEffect(() => {
    // Fetch invoice and company info together
    const fetchData = async () => {
      try {
        // Fetch invoice
        const invoiceRes = await fetch(`https://backend.hira-tech.co.uk/order/invoice/${id}/`);
        if (!invoiceRes.ok) throw new Error("Failed to fetch invoice data");
        const invoiceData = await invoiceRes.json();
        setInvoice(invoiceData);

        // Fetch company info
        const companyRes = await fetch(`https://backend.hira-tech.co.uk/order/company-invoice-details/`);
        if (!companyRes.ok) throw new Error("Failed to fetch company info");
        const companyData = await companyRes.json();
        setCompany(companyData);

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

// InvoiceDetails.js - Updated Email Functions Only

// Generate PDF for email - returns base64 string without data URI prefix
const generatePDFForEmail = () => {
    if (!invoice || !company) return null;

    const doc = new jsPDF("p", "mm", "a4");
    const logo = "/logo.ico";
    const invoiceNumber = `${invoice.invoice_number}`;
    const trackingNumber = invoice.order_details?.order_Tracking_Id || "N/A";

    // Add logo
    try {
      doc.addImage(logo, "PNG", 7, 10, 60, 30);
    } catch (e) {
      console.warn("Logo not found, skipping");
    }

    doc.text(" ", 10, 70);
    const headerX = 12;
    let y = 45;

    const companyName = company?.company_name || '';
    const companyAddress1 = company?.address_line1 || '';
    const companyAddress2 = company?.address_line2 || '';

    // Company info
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, headerX, y);
    doc.setFontSize(10);
    y += 7;
    doc.text(companyAddress1, headerX, y);
    y += 5;
    doc.text(companyAddress2, headerX, y);
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`Invoice #: ${invoiceNumber}`, headerX, y);

    const customerName = invoice.customer
      ? `${invoice.customer.first_Name || ''} ${invoice.customer.last_Name || ''}`
      : "Unknown Customer";

    const customerPhone = invoice.customer
      ? `${invoice.customer.phone_Number || "N/A"}`
      : "N/A";

    const addressParts = [];
    if (invoice?.address) {
      if (invoice.address.house_Number) addressParts.push(invoice.address.house_Number);
      if (invoice.address.street_Number) addressParts.push(invoice.address.street_Number);
      if (invoice.address.area) addressParts.push(invoice.address.area);
      if (invoice.address.city) addressParts.push(invoice.address.city);
    }
    const deliveryAddress = addressParts.length > 0
      ? addressParts.join(' ')
      : "No delivery address available";

    y += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Ship-To-Party", headerX, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.text(customerName, headerX, y);
    y += 5;

    const addressLines = doc.splitTextToSize(deliveryAddress, 80);
    addressLines.forEach((line) => {
      doc.text(line, headerX, y);
      y += 5;
    });

    doc.text(`Contact: ${customerPhone}`, headerX, y);
    y += 5;

    if (invoice.order_details?.courier_service) {
      doc.text(`Courier Service: ${invoice.order_details.courier_service}`, headerX, y);
      y += 5;
    }

    // Invoice box
    const boxX = 120;
    const boxWidth = 75;
    const boxHeight = 70;
    doc.setDrawColor(0, 74, 173);
    doc.rect(boxX, 40, boxWidth, boxHeight);

    doc.setFillColor(0, 74, 173);
    doc.rect(boxX, 40, boxWidth, 10, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", boxX + 5, 47);
    doc.text(invoiceNumber, boxX + boxWidth - 5, 47, { align: "right" });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    let invoiceY = 60;

    doc.text(`Date: ${invoice.issue_date || ''}`, boxX + 5, invoiceY);
    invoiceY += 5;
    doc.text("Page: 1 of 1", boxX + 5, invoiceY);
    invoiceY += 5;

    if (invoice.order_details) {
      doc.text(`Order ID: ${invoice.order_details.order_Id || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Tracking ID: ${trackingNumber}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Courier Service: ${invoice.order_details.courier_service || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
    }

    doc.text(
      `Contact: ${invoice.customer ? invoice.customer.email || '' : "Unknown"}`,
      boxX + 5,
      invoiceY
    );
    invoiceY += 5;

    if (invoice.order_details) {
      doc.text(
        `Customer Reference: ${invoice.order_details.customer_Reference || "N/A"}`,
        boxX + 5,
        invoiceY
      );
    }
    invoiceY += 5;

    doc.setDrawColor(0, 74, 173);
    doc.line(boxX, invoiceY + 2, boxX + boxWidth, invoiceY + 2);
    invoiceY += 7;

    if (invoice.order_details) {
      doc.text(`Agent: ${invoice.order_details.agent_Name || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Mobile phone: ${invoice.order_details.agent_number || ''}`, boxX + 5, invoiceY);
    }

    const tableData = invoice.ordered_products?.map((item) => [
      item.product_Id?.product_Id || "N/A",
      item.product_Id?.product_Name || "N/A",
      item.quantity || "N/A",
      item.price_per_unit ? `Rs.${item.price_per_unit}` : "0",
      item.total_price ? `Rs.${item.total_price}` : "0",
    ]) || [];

    autoTable(doc, {
      startY: 120,
      head: [["Item", "Material/Description", "Quantity", "Price/SU", "Value"]],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: "#004aad", textColor: 255 },
    });

    const finalY = doc.lastAutoTable.finalY || 120;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 74, 173);

    if (invoice.order_details?.subtotal) {
      doc.text("Net Value: Rs." + invoice.order_details.subtotal, 150, finalY + 10);
    }

    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(8);
    doc.text("Terms of payment: 30 days end-of-month", 10, finalY + 35);

    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(10, finalY + 45, 200, finalY + 45);

    const footerY = finalY + 50;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    if (company) {
      doc.text(
        `Website: ${company.website || ''} - E-Mail: ${company.email || ''}`,
        105,
        footerY,
        { align: "center" }
      );
    }

    const bankDetailsY = footerY + 8;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Bank Details:", 10, bankDetailsY);
    doc.setFont("helvetica", "normal");

    let currentY = bankDetailsY + 4;

    if (company) {
      doc.text(`Bank Name: ${company.bank_name || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Account Title: ${company.account_title || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Account Number: ${company.account_number || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Branch: ${company.branch || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`IBAN: ${company.iban || ''}`, 10, currentY);
    }

    // Return ONLY the base64 string (without data URI prefix)
    const dataUri = doc.output('datauristring');
    return dataUri.split(',')[1];
};

// Handle sending email
const handleSendEmail = async () => {
    if (!invoice || !company) return;

    const customerEmail = invoice.customer?.email;

    if (!customerEmail && !customEmail) {
      setShowEmailInput(true);
      return;
    }

    const recipientEmail = customEmail || customerEmail;

    setIsSendingEmail(true);
    setEmailStatus(null);

    try {
      // Generate PDF
      const pdfBase64 = generatePDFForEmail();

      if (!pdfBase64) {
        throw new Error("Failed to generate PDF");
      }

      const token = localStorage.getItem('access_token') || localStorage.getItem('token');

      // Create form data or JSON - use JSON for smaller size
      const requestBody = JSON.stringify({
        pdf_base64: pdfBase64,
        recipient_email: recipientEmail,
        note: emailNote.trim(), // NEW: optional note, sent as empty string if not filled
      });

      console.log('Sending email, PDF size:', Math.round(pdfBase64.length / 1024), 'KB');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

      const response = await fetch(
        `https://backend.hira-tech.co.uk/order/invoice/${invoice.invoice_number}/send-email/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: requestBody,
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.success) {
        const sentInfo = data.saved_to_sent
          ? ' ✅ Copy saved to Sent folder'
          : ' ⚠️ Email sent but not saved to Sent folder';

        setEmailStatus({
          success: true,
          message: `Invoice sent successfully to ${recipientEmail}${sentInfo}`
        });
        setShowEmailInput(false);
        setCustomEmail('');
        setEmailNote(''); // NEW: reset note after successful send
        setTimeout(() => setEmailStatus(null), 8000);
      } else {
        setEmailStatus({
          success: false,
          message: data.message || 'Failed to send email'
        });
      }

    } catch (error) {
      console.error('Error sending email:', error);

      if (error.name === 'AbortError') {
        setEmailStatus({
          success: false,
          message: 'Request timed out. The PDF might be too large. Please try again.'
        });
      } else {
        setEmailStatus({
          success: false,
          message: 'Error sending email: ' + (error.message || 'Connection failed. Please check if the server is running.')
        });
      }
    } finally {
      setIsSendingEmail(false);
    }
};

// NEW: Always open the panel so the user can optionally add a note before sending.
// If the customer already has an email on file, it's pre-filled into the input.
const handleEmailClick = () => {
    const customerEmail = invoice?.customer?.email;

    if (customerEmail && !customEmail) {
      setCustomEmail(customerEmail);
    }
    setShowEmailInput(true);
};

  const handleDownload = () => {
    if (!invoice || !company) return;

    const doc = new jsPDF("p", "mm", "a4");
    const logo = "/logo.ico";
    const invoiceNumber = `${invoice.invoice_number}`;
    const trackingNumber = invoice.order_details?.order_Tracking_Id || "N/A";
    const filename = `${trackingNumber}.pdf`;

    // ... (rest of handleDownload is same as before, I'll keep it as is)
    doc.addImage(logo, "PNG", 7, 10, 60, 30);
    doc.text(" ", 10, 70);
    const headerX = 12;
    let y = 45;

    const companyName = company?.company_name || '';
    const companyAddress1 = company?.address_line1 || '';
    const companyAddress2 = company?.address_line2 || '';

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, headerX, y);
    doc.setFontSize(10);
    y += 7;
    doc.text(companyAddress1, headerX, y);
    y += 5;
    doc.text(companyAddress2, headerX, y);
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`Invoice #: ${invoiceNumber}`, headerX, y);

    const customerName = invoice.customer
      ? `${invoice.customer.first_Name || ''} ${invoice.customer.last_Name || ''}`
      : "Unknown Customer";

    const customerPhone = invoice.customer
      ? `${invoice.customer.phone_Number || "N/A"}`
      : "N/A";

    const addressParts = [];
    if (invoice?.address) {
      if (invoice.address.house_Number) addressParts.push(invoice.address.house_Number);
      if (invoice.address.street_Number) addressParts.push(invoice.address.street_Number);
      if (invoice.address.area) addressParts.push(invoice.address.area);
      if (invoice.address.city) addressParts.push(invoice.address.city);
    }
    const deliveryAddress = addressParts.length > 0
      ? addressParts.join(' ')
      : "No delivery address available";

    y += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Ship-To-Party", headerX, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.text(customerName, headerX, y);
    y += 5;

    const addressLines = doc.splitTextToSize(deliveryAddress, 80);
    addressLines.forEach((line) => {
      doc.text(line, headerX, y);
      y += 5;
    });

    doc.text(`Contact: ${customerPhone}`, headerX, y);
    y += 5;

    if (invoice.order_details?.courier_service) {
      doc.text(`Courier Service: ${invoice.order_details.courier_service}`, headerX, y);
      y += 5;
    }

    const boxX = 120;
    const boxWidth = 75;
    const boxHeight = 70;
    doc.setDrawColor(0, 74, 173);
    doc.rect(boxX, 40, boxWidth, boxHeight);

    doc.setFillColor(0, 74, 173);
    doc.rect(boxX, 40, boxWidth, 10, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", boxX + 5, 47);
    doc.text(invoiceNumber, boxX + boxWidth - 5, 47, { align: "right" });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    let invoiceY = 60;

    doc.text(`Date: ${invoice.issue_date || ''}`, boxX + 5, invoiceY);
    invoiceY += 5;
    doc.text("Page: 1 of 1", boxX + 5, invoiceY);
    invoiceY += 5;

    if (invoice.order_details) {
      doc.text(`Order ID: ${invoice.order_details.order_Id || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Tracking ID: ${invoice.order_details.order_Tracking_Id || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Courier Service: ${invoice.order_details.courier_service || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
    }

    doc.text(
      `Contact: ${invoice.customer ? invoice.customer.email || '' : "Unknown"}`,
      boxX + 5,
      invoiceY
    );
    invoiceY += 5;

    if (invoice.order_details) {
      doc.text(
        `Customer Reference: ${invoice.order_details.customer_Reference || "N/A"}`,
        boxX + 5,
        invoiceY
      );
    }
    invoiceY += 5;

    doc.setDrawColor(0, 74, 173);
    doc.line(boxX, invoiceY + 2, boxX + boxWidth, invoiceY + 2);
    invoiceY += 7;

    if (invoice.order_details) {
      doc.text(`Agent: ${invoice.order_details.agent_Name || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Mobile phone: ${invoice.order_details.agent_number || ''}`, boxX + 5, invoiceY);
    }

    const tableData = invoice.ordered_products?.map((item) => [
      item.product_Id?.product_Id || "N/A",
      item.product_Id?.product_Name || "N/A",
      item.quantity || "N/A",
      item.price_per_unit ? `Rs.${item.price_per_unit}` : "0",
      item.total_price ? `Rs.${item.total_price}` : "0",
    ]) || [];

    autoTable(doc, {
      startY: 120,
      head: [["Item", "Material/Description", "Quantity", "Price/SU", "Value"]],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: "#004aad", textColor: 255 },
    });

    const finalY = doc.lastAutoTable.finalY || 120;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 74, 173);

    if (invoice.order_details?.subtotal) {
      doc.text("Net Value: Rs." + invoice.order_details.subtotal, 150, finalY + 10);
    }

    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(8);
    doc.text("Terms of payment: 30 days end-of-month", 10, finalY + 35);

    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(10, finalY + 45, 200, finalY + 45);

    const footerY = finalY + 50;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    if (company) {
      doc.text(
        `Website: ${company.website || ''} - E-Mail: ${company.email || ''}`,
        105,
        footerY,
        { align: "center" }
      );
    }

    const bankDetailsY = footerY + 8;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Bank Details:", 10, bankDetailsY);
    doc.setFont("helvetica", "normal");

    let currentY = bankDetailsY + 4;

    if (company) {
      doc.text(`Bank Name: ${company.bank_name || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Account Title: ${company.account_title || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Account Number: ${company.account_number || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Branch: ${company.branch || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`IBAN: ${company.iban || ''}`, 10, currentY);
    }

    if (currentY > 280) {
      doc.addPage();
    }

    doc.save(filename);
  };


  const handlePrint = () => {
    // ... (handlePrint remains unchanged)
    if (!invoice || !company) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();

    const invoiceNumber = invoice.invoice_number;
    const trackingNumber = invoice.order_details?.order_Tracking_Id || "N/A";
    const customerName = invoice.customer
      ? `${invoice.customer.first_Name || ''} ${invoice.customer.last_Name || ''}`
      : "Unknown Customer";

    const customerPhone = invoice.customer
      ? `${invoice.customer.phone_Number || "N/A"}`
      : "N/A";

    const customerEmail = invoice.customer ? invoice.customer.email || '' : "Unknown";

    const addressParts = [];
    if (invoice?.address) {
      if (invoice.address.house_Number) addressParts.push(invoice.address.house_Number);
      if (invoice.address.street_Number) addressParts.push(invoice.address.street_Number);
      if (invoice.address.area) addressParts.push(invoice.address.area);
      if (invoice.address.city) addressParts.push(invoice.address.city);
    }
    const deliveryAddress = addressParts.length > 0
      ? addressParts.join(' ')
      : "No delivery address available";

    const productsRows = invoice.ordered_products?.map((item) => `
      <tr>
        <td style="border: 1px solid #ddd; padding: 6px; font-size: 9pt;">${item.product_Id?.product_Id || "N/A"}</td>
        <td style="border: 1px solid #ddd; padding: 6px; font-size: 9pt;">${item.product_Id?.product_Name || "N/A"}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-size: 9pt;">${item.quantity || "N/A"}</td>
      </tr>
    `).join('') || '';

    const logoUrl = `${window.location.origin}/logo.ico`;

    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Delivery Note - ${trackingNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A4; margin: 12mm; }
          body { font-family: Arial, Helvetica, sans-serif; background: white; color: #000; line-height: 1.3; }
          .print-container { width: 100%; }
          .logo-row { margin-bottom: 20px; }
          .logo-row img { width: 200px; height: auto; display: block; }
          .company-delivery-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 15px; }
          .company-info { flex: 1; }
          .company-name { font-size: 16pt; font-weight: bold; margin-bottom: 5px; }
          .company-address { font-size: 10pt; line-height: 1.3; margin-bottom: 5px; }
          .invoice-number-print { font-size: 11pt; font-weight: bold; margin-top: 5px; }
          .invoice-box { width: 75mm; border: 1px solid #004aad; flex-shrink: 0; }
          .invoice-box-header { background-color: #004aad; color: white; padding: 5px 8px; font-weight: bold; font-size: 12pt; display: flex; justify-content: space-between; }
          .invoice-box-content { padding: 8px; font-size: 10pt; }
          .invoice-box-content p { margin: 2px 0; line-height: 1.3; }
          .divider { border-top: 1px solid #004aad; margin: 5px 0; }
          .bottom-row { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 15px; }
          .ship-to-section { flex: 1; margin-top: -150px; }
          .ship-to-title { font-weight: bold; margin-bottom: 5px; }
          .ship-to-details { font-size: 10pt; line-height: 1.3; }
          .spacer { width: 75mm; flex-shrink: 0; }
          .products-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          .products-table th { background-color: #004aad; color: white; padding: 6px; text-align: left; font-size: 10pt; font-weight: bold; border: 1px solid #004aad; }
          .products-table th:nth-child(3) { text-align: center; }
          .products-table td { border: 1px solid #ddd; padding: 6px; font-size: 9pt; }
          .products-table td:nth-child(3) { text-align: center; }
          .terms { font-size: 8pt; margin: 25px 0 5px 0; }
          .footer-line { border-top: 0.5px solid #000; margin: 10px 0; }
          .website-email { text-align: center; font-size: 8pt; margin: 5px 0; }
          @media print {
            body { margin: 0; padding: 0; }
            .products-table th { background-color: #004aad !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .invoice-box-header { background-color: #004aad !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <div class="logo-row">
            <img src="${logoUrl}" alt="Company Logo" onerror="this.style.display='none'" />
          </div>
          <div class="company-delivery-row">
            <div class="company-info">
              <div class="company-name">${company?.company_name || ''}</div>
              <div class="company-address">
                ${company?.address_line1 || ''}<br>
                ${company?.address_line2 || ''}
              </div>
            </div>
            <div class="invoice-box">
              <div class="invoice-box-header">
                <span>Delivery Note</span>
                <span>${trackingNumber}</span>
              </div>
              <div class="invoice-box-content">
                <p>Date: ${invoice.issue_date || ''}</p>
                <p>Page: 1 of 1</p>
                ${invoice.order_details ? `
                  <p>Order ID: ${invoice.order_details.order_Id || ''}</p>
                  <p>Tracking ID: ${trackingNumber}</p>
                  <p>Courier Service: ${invoice.order_details.courier_service || ''}</p>
                ` : ''}
                <p>Contact: ${customerEmail}</p>
                ${invoice.order_details ? `
                  <p>Customer Reference: ${invoice.order_details.customer_Reference || "N/A"}</p>
                ` : ''}
                <div class="divider"></div>
                ${invoice.order_details ? `
                  <p>Agent: ${invoice.order_details.agent_Name || ''}</p>
                  <p>Mobile phone: ${invoice.order_details.agent_number || ''}</p>
                ` : ''}
              </div>
            </div>
          </div>
          <div class="bottom-row">
            <div class="ship-to-section">
              <div class="ship-to-title">Ship-To-Party</div>
              <div class="ship-to-details">
                ${customerName}<br>
                ${deliveryAddress}<br>
                Contact: ${customerPhone}${invoice.order_details?.courier_service ? `<br>Courier Service: ${invoice.order_details.courier_service}` : ''}
              </div>
            </div>
            <div class="spacer"></div>
          </div>
          <table class="products-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Material/Description</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${productsRows}
            </tbody>
          </table>
          <div class="terms">Terms of payment: 30 days end-of-month</div>
          <div class="footer-line"></div>
          <div class="website-email">
            Website: ${company?.website || ''} - E-Mail: ${company?.email || ''}
          </div>
        </div>
      </body>
      </html>
    `;

    doc.write(printHTML);
    doc.close();

    setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    }, 500);

    iframe.contentWindow.onafterprint = () => {
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 100);
    };
  };

  // const handleShareClick = () => {
  //   setIsShareModalOpen(true);
  // };

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!invoice) return <p>No invoice data found</p>;

  return (
    <>
      <div className="invoice-container">
        <div className="link">
          <ul className="return-link">
            <li>
              <Link to="/admin/monthly-invoices">
                <span className="return-text">
                  <FaChevronLeft className="chevron-icon" /> Return to Statements
                </span>
              </Link>
            </li>
          </ul>
        </div>

        <div className="header-container">
          <Stack direction="column" spacing={2}>
            <h2 className="title">
              <strong style={{ color: "#004aad" }}>Invoice Number:</strong>{" "}
              {invoice.invoice_number}
            </h2>
            <h3 className="title">
              <strong style={{ color: "#004aad", marginLeft: "20px" }}>
                Customer:
              </strong>{" "}
              {invoice.customer
                ? `${invoice.customer.first_Name || ''} ${invoice.customer.last_Name || ''}`
                : "Unknown"}
            </h3>
            {invoice.order_details?.order_Tracking_Id && (
              <h3 className="title">
                <strong style={{ color: "#004aad", marginLeft: "20px" }}>
                  Tracking Number:
                </strong>{" "}
                {invoice.order_details.order_Tracking_Id}
              </h3>
            )}
          </Stack>

          {/* Button Group with Email Button Added */}
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            {/* Email Button */}
            <button
              onClick={handleEmailClick}
              disabled={isSendingEmail}
              style={{
                background: 'none',
                border: 'none',
                cursor: isSendingEmail ? 'wait' : 'pointer',
                opacity: isSendingEmail ? 0.6 : 1,
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Send Invoice via Email"
            >
              {isSendingEmail ? (
                <span style={{ fontSize: '20px', animation: 'spin 1s linear infinite' }}>⏳</span>
              ) : (
                <FaEnvelope style={{ color: "#ffc107", fontSize: "24px", width: "24px", height: "24px" }} />
              )}
            </button>

            {/* <FaShareAlt
              onClick={handleShareClick}
              style={{ color: "#28a745", cursor: "pointer", fontSize: "24px", width: "24px", height: "24px" }}
            /> */}
            <FaPrint
              onClick={handlePrint}
              style={{ color: "#dc3545", cursor: "pointer", fontSize: "24px", width: "24px", height: "24px" }}
            />
            <FaDownload
              onClick={handleDownload}
              style={{ color: "#004aad", cursor: "pointer", fontSize: "24px", width: "24px", height: "24px" }}
            />
          </div>
        </div>

        {/* Email Input Panel (now always shown on email-button click, so an optional note can be added) */}
        {showEmailInput && (
          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap'
            }}>
              <label style={{ fontWeight: 'bold', color: '#004aad', minWidth: '120px' }}>
                Recipient Email:
              </label>
              <input
                type="email"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                placeholder="Enter email address"
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              />
            </div>

            {/* NEW: Optional note field */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              flexWrap: 'wrap'
            }}>
              <label style={{ fontWeight: 'bold', color: '#004aad', minWidth: '120px', marginTop: '8px' }}>
                Note (optional):
              </label>
              <textarea
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                placeholder="Add a note to include in the email (optional)"
                rows={3}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleSendEmail}
                disabled={!customEmail || isSendingEmail}
                style={{
                  backgroundColor: '#004aad',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '4px',
                  cursor: customEmail && !isSendingEmail ? 'pointer' : 'not-allowed',
                  opacity: customEmail && !isSendingEmail ? 1 : 0.6,
                  fontWeight: 'bold',
                }}
              >
                {isSendingEmail ? 'Sending...' : 'Send'}
              </button>
              <button
                onClick={() => {
                  setShowEmailInput(false);
                  setCustomEmail('');
                  setEmailNote(''); // NEW: clear note on cancel
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Email Status Message */}
        {emailStatus && (
          <div style={{
            padding: '12px 15px',
            marginBottom: '20px',
            borderRadius: '8px',
            backgroundColor: emailStatus.success ? '#d4edda' : '#f8d7da',
            color: emailStatus.success ? '#155724' : '#721c24',
            border: `1px solid ${emailStatus.success ? '#c3e6cb' : '#f5c6cb'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>
              {emailStatus.success ? '✅ ' : '❌ '}
              {emailStatus.message}
            </span>
            <button
              onClick={() => setEmailStatus(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: emailStatus.success ? '#155724' : '#721c24',
              }}
            >
              ✕
            </button>
          </div>
        )}

        <div className="main-container">
          <div className="order-summary">
            <h3>Order Details</h3>
            {invoice.order_details && (
              <>
                <p>
                  <strong>Order ID:</strong> {invoice.order_details.order_Id}
                </p>
                <p>
                  <strong>Tracking ID:</strong>{" "}
                  {invoice.order_details.order_Tracking_Id}
                </p>
                <p>
                  <strong>Order Status:</strong> {invoice.order_details.order_Status}
                </p>
                <p>
                  <strong>Placement Date:</strong>{" "}
                  {invoice.order_details.order_Placment_Date}
                </p>
                <p>
                  <strong>Delivery Date:</strong>{" "}
                  {invoice.order_details.order_Delivery_Date}
                </p>
                <p>
                  <strong>Delivery Method:</strong>{" "}
                  {invoice.order_details.deliver_Method}
                </p>
                <p>
                  <strong>Customer Reference:</strong>{" "}
                  {invoice.order_details.customer_Reference}
                </p>
                <p>
                  <strong>Agent:</strong> {invoice.order_details.agent_Name}
                </p>
                <p>
                  <strong>Agent Phone:</strong> {invoice.order_details.agent_number}
                </p>
              </>
            )}
          </div>

          <div className="order-summary">
            <h3>Order Amount</h3>
            {invoice.order_details && (
              <>
                <p>
                  <strong>Subtotal:</strong> {formatPrice(invoice.order_details.subtotal)}
                </p>
                <p>
                  <strong>Discount:</strong> {formatPrice(invoice.order_details.discount)}
                </p>
                <p>
                  <strong>Delivery Charges:</strong> {formatPrice(invoice.order_details.delivery_Charges)}
                </p>
                <p>
                  <strong>Total:</strong> {formatPrice(invoice.order_details.total_Amount)}
                </p>
              </>
            )}
            <p>
              <strong>Payment Due:</strong> {formatPrice(invoice.payment_due)}
            </p>
            <p>
              <span
                style={{
                  display: "inline-block",
                  padding: "5px 40px",
                  border: "3px solid #008000",
                  borderRadius: "4px",
                  color: "#008000",
                  fontWeight: "bold",
                  marginTop: "20px",
                }}
              >
                {invoice.payment_status}
              </span>
            </p>
          </div>

          <div className="order-products">
            <h3>Ordered Products</h3>
            {invoice.ordered_products && invoice.ordered_products.map((product) => {
              const imageUrl = product.product_Id?.imageGallery?.image1
                ? `https://backend.hira-tech.co.uk${product.product_Id.imageGallery.image1}`
                : "";
              return (
                <div key={product.id} className="product-container">
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt={product.product_Id?.product_Name || 'Product'}
                      className="order-image"
                    />
                  )}
                  <div className="product-details">
                    <p>
                      <strong>Name:</strong> {product.product_Id?.product_Name || 'N/A'}
                    </p>
                    <p>
                      <strong>SKU:</strong> {product.product_Id?.SKU || 'N/A'}
                    </p>
                    <p>
                      <strong>Quantity:</strong> {product.quantity}
                    </p>
                    <p>
                      <strong>Price per unit:</strong> {formatPrice(product.price_per_unit)}
                    </p>
                    <p>
                      <strong>Total Price:</strong> {formatPrice(product.total_price)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ship-to-section" style={{ marginTop: "30px", background: "#f9f9f9", padding: "20px", borderRadius: "8px" }}>
          <h3 style={{ color: "#004aad", borderBottom: "1px solid #ddd", paddingBottom: "10px" }}>Ship-To Party</h3>
          <p><strong>Name:</strong> {invoice.customer ? `${invoice.customer.first_Name || ''} ${invoice.customer.last_Name || ''}` : "Unknown Customer"}</p>
          <p><strong>Address:</strong> {
            (() => {
              const addressPartsForDisplay = [];
              if (invoice?.address) {
                if (invoice.address.house_Number) addressPartsForDisplay.push(invoice.address.house_Number);
                if (invoice.address.street_Number) addressPartsForDisplay.push(invoice.address.street_Number);
                if (invoice.address.area) addressPartsForDisplay.push(invoice.address.area);
                if (invoice.address.city) addressPartsForDisplay.push(invoice.address.city);
              }
              return addressPartsForDisplay.length > 0 ? addressPartsForDisplay.join(' ') : "No delivery address available";
            })()
          }</p>
          <p><strong>Contact Number:</strong> {invoice.customer ? invoice.customer.phone_Number || "N/A" : "N/A"}</p>
          {invoice.order_details?.order_Tracking_Id && (
            <p><strong>Tracking Number:</strong> {invoice.order_details.order_Tracking_Id}</p>
          )}
        </div>

        <style jsx>{`
          /* Main Layout */
          .invoice-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
          }

          .link {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }

          .return-link {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .return-text {
            color: #004aad;
            display: flex;
            align-items: center;
            gap: 5px;
          }

          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
            padding-bottom: 15px;
            border-bottom: 1px solid #eee;
          }

          .title {
            margin: 0;
            color: #333;
          }

          .download-icon, .print-icon, .email-icon {
            transition: transform 0.2s;
          }

          .download-icon:hover, .print-icon:hover, .email-icon:hover {
            transform: scale(1.1);
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .main-container {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }

          .order-summary {
            background: #f9f9f9;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .order-summary h3 {
            margin-top: 0;
            color: #004aad;
            border-bottom: 1px solid #ddd;
            padding-bottom: 10px;
          }

          .order-summary p {
            margin: 8px 0;
          }

          .order-products {
            grid-column: span 2;
          }

          .product-container {
            display: flex;
            gap: 20px;
            padding: 15px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            margin-bottom: 15px;
          }

          .order-image {
            width: 150px;
            height: 150px;
            object-fit: contain;
            border-radius: 4px;
            border: 1px solid #eee;
          }

          .product-details {
            flex: 1;
          }

          .product-details p {
            margin: 5px 0;
          }

          .ship-to-section {
            margin-top: 30px;
            background: #f9f9f9;
            padding: 20px;
            border-radius: 8px;
          }

          .ship-to-section h3 {
            color: #004aad;
            border-bottom: 1px solid #ddd;
            padding-bottom: 10px;
          }

          .ship-to-section p {
            margin: 8px 0;
          }

          @media (max-width: 768px) {
            .main-container {
              grid-template-columns: 1fr;
            }

            .order-products {
              grid-column: span 1;
            }

            .product-container {
              flex-direction: column;
            }

            .order-image {
              width: 100%;
              height: auto;
            }
          }
        `}</style>
      </div>
{/* 
      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        invoice={invoice}
        company={company}
      /> */}
    </>
  );
};

export default InvoiceDetails;